import type { Policy, PolicyList } from "@eai-toolkit/client";
import { AllowedApi, PolicyApi, UserApi } from "@eai-toolkit/client";
import type { Comparison, Filters } from "@store/utils/queryStateUtils";
import { filtersToQ } from "@store/utils/queryUtils";
import { constructOrderParameter } from "@store/utils/sortUtils";

import { EXPIRING_SOON_DAYS } from "@helpers/constants";

import { fields } from "../../modules/app/permissions/data/fields";
import {
  deriveUniqueAccounts,
  isValidPolicy,
  mapPolicyToRule,
  type PermissionsAccount,
  type Rule,
  type RuleList,
  type RuleSource,
} from "../../modules/app/permissions/data/types";
import { hydrateRulesQueryStateFromString } from "../queryState";
import { baseApi, handleQueryError } from "./baseApi";
import { axiosInstance, getAllResources } from "./utils";

const userApi = new UserApi(undefined, undefined, axiosInstance);
const policyApi = new PolicyApi(undefined, undefined, axiosInstance);
const allowedApi = new AllowedApi(undefined, undefined, axiosInstance);

// Policy deletion is gated on `policy:rm` (PermissionsV2) or `policy:set`
// (legacy); we can't tell which an environment runs, so either one suffices.
const POLICY_DELETE_ACTIONS = ["policy:rm", "policy:set"] as const;

function getOrderField(fieldName: string): string {
  const fieldDef = fields[fieldName as keyof typeof fields];
  return fieldDef && "orderField" in fieldDef ? fieldDef.orderField : fieldName;
}

export const userRulesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getUserPolicies: builder.query<
      RuleList,
      // queryKey is a serialized RulesQueryState — a string keeps RTK Query's
      // cache key stable. expiringSoon/expired add a server-side expiration
      // filter (the two quick filters) so paging and counts stay server-driven.
      {
        queryKey: string;
        userId: string;
        expiringSoon?: boolean;
        expired?: boolean;
      }
    >({
      queryFn: async ({ queryKey, userId, expiringSoon, expired }) => {
        try {
          const queryState = hydrateRulesQueryStateFromString(queryKey);
          const {
            paging: { offset, limit },
            query: { filters, sort },
          } = queryState;

          const order = constructOrderParameter(
            sort.fieldName,
            sort.direction,
            getOrderField,
          );

          // The "Expiring soon" and "Expired" quick filters map to an expiration
          // comparison. Expiring soon = active rows expiring on or before the
          // window's end (already-expired and null-expiration rows fall out
          // naturally, since inactive rows are excluded without includeInactive).
          // Expired = past-expiration rows, which requires includeInactive to
          // surface them at all. The bound is relative to "now", so it's computed
          // here (not persisted in the URL query state) via the comparisons slot.
          const now = new Date();
          const expirationComparison: Comparison | null = expiringSoon
            ? {
                operator: "le",
                value: new Date(
                  now.getTime() + EXPIRING_SOON_DAYS * 24 * 60 * 60 * 1000,
                ).toISOString(),
              }
            : expired
              ? { operator: "lt", value: now.toISOString() }
              : null;
          if (expirationComparison) {
            // Keyed by the frontend field name `expires`; filtersToQ resolves it
            // to the backend `expiration` via getOrderField (same mapping as order).
            const existing = filters.get("expires");
            filters.set("expires", {
              valueList: existing?.valueList ?? [],
              booleanValueList: existing?.booleanValueList ?? [],
              fuzzyMatch: existing?.fuzzyMatch ?? null,
              range: existing?.range ?? [],
              comparisons: [expirationComparison],
            });
          }
          const q = filtersToQ(filters, getOrderField);

          // recursive=true resolves grants inherited via teams/roles. We use
          // v1UserPolicyGet (not v1MePolicyGet, whose SDK type omits recursive),
          // so callers must resolve the user id first. X-API-Version: 2 keeps the
          // { items, metadata } envelope instead of a bare Policy[].
          const response = await userApi.v1UserPolicyGet(
            {
              userId,
              limit,
              offset,
              order,
              q,
              recursive: true,
              human: true,
              includeInactive: expired || undefined,
            },
            { headers: { "X-API-Version": "2" } },
          );

          const policyList = response.data as PolicyList;
          const items = (policyList.items ?? [])
            .filter(isValidPolicy)
            .map(mapPolicyToRule);

          return {
            data: {
              ...policyList,
              items,
            },
          };
        } catch (error) {
          return handleQueryError(error);
        }
      },
      providesTags: (result, _error, { userId }) =>
        result
          ? [
              ...result.items.map(({ policyId }) => ({
                type: "Policy" as const,
                id: policyId,
              })),
              { type: "Policy" as const, id: "LIST" },
              { type: "Policy" as const, id: userId },
            ]
          : [
              { type: "Policy" as const, id: "LIST" },
              { type: "Policy" as const, id: userId },
            ],
    }),

    // All pages, unfiltered, to populate the Account filter. Accounts aren't a
    // policy field, so we derive {id, name} from each rule's expression + resource.
    getAllUserPolicyAccounts: builder.query<
      PermissionsAccount[],
      { userId: string }
    >({
      queryFn: async ({ userId }) => {
        try {
          const { items } = await getAllResources<Policy>(
            (params) =>
              userApi.v1UserPolicyGet(
                { userId, ...params, recursive: true, human: true },
                { headers: { "X-API-Version": "2" } },
              ) as unknown as Promise<{ data: PolicyList }>,
          );
          const rules = items.filter(isValidPolicy).map(mapPolicyToRule);
          return { data: deriveUniqueAccounts(rules) };
        } catch (error) {
          return handleQueryError(error);
        }
      },
      providesTags: (_result, _error, { userId }) => [
        { type: "Policy" as const, id: "LIST" },
        { type: "Policy" as const, id: userId },
      ],
    }),

    // All actions the user holds for a resource across every page (the table
    // shows one). "Request access" visibility needs the full set, or a later
    // page's covering grant would be missed (see userHasAllRequestableAccess).
    // expressionValues is the resource's ancestry.
    getAllUserResourceActions: builder.query<
      string[],
      { userId: string; expressionValues: string[] }
    >({
      queryFn: async ({ userId, expressionValues }) => {
        try {
          const filters: Filters<Rule> = new Map();
          filters.set("expression", {
            valueList: expressionValues,
            booleanValueList: [],
            fuzzyMatch: null,
            range: [],
          });
          const q = filtersToQ(filters);
          const { items } = await getAllResources<Policy>(
            (params) =>
              userApi.v1UserPolicyGet(
                { userId, ...params, q, recursive: true, human: true },
                { headers: { "X-API-Version": "2" } },
              ) as unknown as Promise<{ data: PolicyList }>,
          );
          return {
            data: items.filter(isValidPolicy).map((policy) => policy.action),
          };
        } catch (error) {
          return handleQueryError(error);
        }
      },
      providesTags: (_result, _error, { userId }) => [
        { type: "Policy" as const, id: "LIST" },
        { type: "Policy" as const, id: userId },
      ],
    }),

    // Whether the user may delete policies on each parent (self + each role/team
    // they belong to). Keyed by full name, since a row resolves to its parent via
    // source.id (the parent's full name under human=true).
    getDeletablePolicyParents: builder.query<
      Record<string, boolean>,
      { parents: { fullName: string; urn: string }[] }
    >({
      queryFn: async ({ parents }) => {
        try {
          const entries = await Promise.all(
            parents.map(async ({ fullName, urn }) => {
              const results = await Promise.allSettled(
                POLICY_DELETE_ACTIONS.map((action) =>
                  allowedApi.v1AllowedPost({
                    allowed: { resource: urn, action },
                  }),
                ),
              );
              // v1AllowedPost rejects (403) when not allowed, resolves when it is.
              return [
                fullName,
                results.some((r) => r.status === "fulfilled"),
              ] as const;
            }),
          );
          return { data: Object.fromEntries(entries) };
        } catch (error) {
          return handleQueryError(error);
        }
      },
      // Subscribe to the Policy LIST tag so deletePolicy's invalidation re-runs
      // the allowed-checks; otherwise the permission map goes stale for the
      // session (e.g. after the user's team/role membership changes).
      providesTags: [{ type: "Policy" as const, id: "LIST" }],
    }),

    // The endpoint depends on the grant's source: direct grants go through the
    // user, inherited ones through the team/role they're attached to (source.id).
    // Deleting by policyId removes every row sharing it (a policy can surface via
    // multiple inheritance paths in the recursive listing).
    deletePolicy: builder.mutation<
      void,
      { policyId: string; source: RuleSource; userId: string }
    >({
      queryFn: async ({ policyId, source, userId }) => {
        try {
          switch (source.kind) {
            case "team":
              if (!source.id) throw new Error("Missing team id for policy");
              await policyApi.v1TeamPolicyDelete({
                teamId: source.id,
                policyId,
              });
              break;
            case "role":
              if (!source.id) throw new Error("Missing role id for policy");
              await policyApi.v1RolePolicyDelete({
                roleId: source.id,
                policyId,
              });
              break;
            case "direct":
              await policyApi.v1UserPolicyDelete({
                userId,
                policyId,
              });
              break;
            default:
              throw new Error("Cannot delete a policy from an unknown source");
          }
          return { data: undefined };
        } catch (error) {
          return handleQueryError(error);
        }
      },
      invalidatesTags: [{ type: "Policy" as const, id: "LIST" }],
    }),
  }),
});

export const {
  useGetUserPoliciesQuery,
  useGetAllUserPolicyAccountsQuery,
  useGetAllUserResourceActionsQuery,
  useGetDeletablePolicyParentsQuery,
  useDeletePolicyMutation,
} = userRulesApi;
